import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, CheckCircleIcon, PhoneIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../components/Button';
interface ContentSection {
  type: 'h2' | 'p' | 'ul' | 'steps';
  text?: string;
  items?: string[];
  stepsTitle?: string;
  steps?: StepItem[];
}
interface ServiceData {
  slug: string;
  heroTitle: string;
  excerpt: string;
  content: ContentSection[];
  quickFacts: {
    label: string;
    value: string;
  }[];
}
const services: ServiceData[] = [{
  slug: 'private-mortgages',
  heroTitle: 'Access the Funds You Need with a Private Mortgage',
  excerpt: "At EquityRich, we specialize in private mortgages that focus on the value of your property, not your personal financial factors like age, income, or credit score. Whether you're looking for a private mortgage for a quick closing, a second mortgage, or need a flexible lending solution that works for your unique situation, we offer competitive equity-based lending that helps you achieve your goals without the traditional restrictions.",
  quickFacts: [{
    label: 'Max LTV',
    value: 'Up to 75%'
  }, {
    label: 'Credit Check',
    value: 'Not Required'
  }, {
    label: 'Income Verification',
    value: 'Not Required'
  }, {
    label: 'Funding Speed',
    value: 'As fast as 48 hrs'
  }],
  content: [{
    type: 'h2',
    text: 'What Is a Private Mortgage?'
  }, {
    type: 'p',
    text: "A private mortgage is a loan that is offered by private lenders rather than banks or other financial institutions. These loans are designed for individuals who may not meet the strict requirements of traditional lenders, such as high credit scores or steady income. At EquityRich, we focus solely on the equity in your property, offering you a loan based on your home's value rather than your personal financial history."
  }, {
    type: 'p',
    text: 'With our private mortgage insurance options, we can also help you protect your investment, ensuring you get the financial security you need. Private mortgage insurance (PMI) helps mitigate the risk for lenders, allowing us to offer more competitive terms, including higher loan amounts, with second mortgages available.'
  }, {
    type: 'h2',
    text: 'Benefits of a Private Mortgage'
  }, {
    type: 'ul',
    items: ['No credit check required — approval based on property equity', 'Flexible terms tailored to your unique financial situation', 'Quick closing — funds available in as little as 48 hours', 'Available for first and second mortgages', 'No income verification required', 'Competitive equity-based lending rates']
  }, {
    type: 'h2',
    text: 'Who Can Benefit from a Private Mortgage?'
  }, {
    type: 'p',
    text: 'Private mortgages are ideal for homeowners who have been turned down by traditional lenders due to credit issues, self-employment income, or other financial circumstances. If you have equity in your home, we can help you access the funds you need.'
  }, {
    type: 'steps',
    stepsTitle: 'The Private Mortgage Process',
    steps: [{
      title: 'Consultation and Eligibility Assessment',
      description: "The process begins with a consultation where we assess the equity in your property. We'll explain how a private mortgage works, discuss your options, and help you determine if this solution is right for you. Since there are no credit checks or income verification required, we can move quickly to help you."
    }, {
      title: 'Property Appraisal',
      description: 'We work with professional appraisers to determine the market value of your home. This will help us calculate how much equity you can access through a private mortgage. Typically, we can offer you up to 70-75% of the value of your home, depending on the property and the loan.'
    }, {
      title: 'Loan Offer and Terms',
      description: "After the property appraisal, we'll present you with a loan offer and clearly explain the terms, including interest rates, loan amount, and repayment schedule. With our private mortgage insurance, we also offer additional protection to secure your investment."
    }, {
      title: 'Signing and Disbursement',
      description: "Once the terms are agreed upon, we move forward with the paperwork and disburse the loan according to the agreed-upon method. Whether you choose a lump sum, line of credit, or regular payments, you'll have the flexibility to use the funds however you need."
    }, {
      title: 'Repayment',
      description: 'Repayment for private mortgages is typically required when the home is sold, or you move out, but we offer flexible solutions to meet your needs. We also offer first and second position mortgages to ensure we have the right option for your financial situation.'
    }]
  }, {
    type: 'h2',
    text: 'Why Choose EquityRich for Your Private Mortgage?'
  }, {
    type: 'p',
    text: "EquityRich is your trusted private mortgage lender, focused on offering solutions that are flexible and based on the value of your property. We specialize in equity-based lending, which means we don't rely on your credit score, income, or age. We offer competitive loan-to-value ratios and provide both first and second mortgages to meet your needs."
  }, {
    type: 'p',
    text: "If you're ready to explore your private mortgage options, use our mortgage calculator to estimate your loan or contact us for a personalized consultation. Whether you're looking for a second mortgage, a home equity loan, or a private mortgage, we're here to help you access the equity in your home and achieve your financial goals."
  }]
}, {
  slug: 'no-income-mortgages',
  heroTitle: "Unlock Your Home's Equity with a No-Income Mortgage",
  excerpt: "At EquityRich, we provide no-income-verification mortgages for homeowners in the Greater Toronto Area (GTA) and beyond. If you're looking for a mortgage solution that doesn't require proof of income, you've come to the right place.",
  quickFacts: [{
    label: 'Income Proof',
    value: 'Not Required'
  }, {
    label: 'Max LTV',
    value: 'Up to 75%'
  }, {
    label: 'Credit Check',
    value: 'Not Required'
  }, {
    label: 'Funding Speed',
    value: 'As fast as 48 hrs'
  }],
  content: [{
    type: 'h2',
    text: 'What Is a No-Income Mortgage?'
  }, {
    type: 'p',
    text: 'A no-income mortgage is a type of loan that allows you to access the equity in your home without providing proof of income. This type of mortgage is ideal for self-employed individuals, retirees, or those with irregular or fluctuating earnings. At EquityRich, we base our lending decisions primarily on the value of your home, rather than requiring traditional income documentation.'
  }, {
    type: 'h2',
    text: 'Benefits of a No-Income Mortgage'
  }, {
    type: 'ul',
    items: ['No income verification or proof of employment required', 'Ideal for self-employed, retired, or commission-based earners', "Approval based on your home's equity and location", "Access up to 75% of your home's appraised value", 'Fast approval and funding in as little as 48 hours', 'No credit check required']
  }, {
    type: 'h2',
    text: 'Who Qualifies?'
  }, {
    type: 'p',
    text: 'If you own a home in Ontario with sufficient equity, you may qualify for a no-income mortgage regardless of your employment status. Our equity-based approach means we look at what your home is worth, not what you earn.'
  }, {
    type: 'steps',
    stepsTitle: 'The No Income Mortgage Process',
    steps: [{
      title: 'Consultation and Eligibility Check',
      description: "The process begins with a consultation to assess the equity in your property. Our experts will walk you through the details of how a no-income-verification mortgage works, helping you understand the options available to you. We'll focus on the value of your home, and we don't require documentation of your income or credit score."
    }, {
      title: 'Property Valuation',
      description: "We'll arrange for a professional appraisal of your property to determine its current market value. This step is crucial, as it helps us calculate how much equity you can access."
    }, {
      title: 'Loan Offer and Terms',
      description: "Once we have your property's value, we'll present you with a personalized loan offer. This will include the loan amount, interest rates, repayment terms, and any applicable fees. Our no-income-verification mortgage lenders will guide you through the details to ensure you're comfortable with the terms before proceeding."
    }, {
      title: 'Signing and Disbursement',
      description: "After reviewing and signing the loan agreement, we'll disburse the funds according to your preferred method — whether as a lump sum, regular payments, or a line of credit. We make the process simple and fast, ensuring that you can access the money you need as soon as possible."
    }, {
      title: 'Repayment',
      description: "With a no-income mortgage, you'll make payments based on the terms of the agreement, but since there are no minimum income or credit score requirements, the process is much more flexible than traditional loans. Repayment is typically due when you sell the property or when you no longer live in the home."
    }]
  }, {
    type: 'h2',
    text: 'Why Choose EquityRich for Your No-Income Mortgage?'
  }, {
    type: 'p',
    text: "EquityRich is committed to providing no-income-verification mortgage solutions that focus on the value of your property. Unlike traditional lenders, we don't require proof of income, credit checks, or minimum age limits. We offer first and second mortgages with competitive loan-to-value ratios of up to 70-75%, giving you access to more of your home's equity."
  }, {
    type: 'p',
    text: "Our flexible no-income mortgage process is designed to work with your unique financial situation, making it easier to access funds without the usual barriers. Whether you're looking for a second mortgage or just need access to extra funds, we have the right solution for you."
  }]
}, {
  slug: 'home-equity-loans',
  heroTitle: 'Access Funds Quickly with a Home Equity Loan',
  excerpt: 'At EquityRich, we specialize in offering home equity loans that allow homeowners to unlock the full potential of their property. Whether you need funds for home improvements, debt consolidation, or other major expenses, a home equity loan can provide you with the financial flexibility you need without the need to sell your home.',
  quickFacts: [{
    label: 'Max LTV',
    value: 'Up to 75%'
  }, {
    label: 'Loan Type',
    value: 'Lump Sum'
  }, {
    label: 'Credit Check',
    value: 'Not Required'
  }, {
    label: 'Funding Speed',
    value: 'As fast as 48 hrs'
  }],
  content: [{
    type: 'h2',
    text: 'What Is a Home Equity Loan?'
  }, {
    type: 'p',
    text: "A home equity loan is a type of loan where you borrow money using the equity in your home as collateral. The equity in your home is the difference between the market value of your property and the amount you still owe on your mortgage. With a home equity mortgage, you can access up to 75% of your home's value, depending on the equity you've built."
  }, {
    type: 'p',
    text: 'Unlike a second mortgage, which provides a separate loan, a home equity loan is typically a lump sum that is repaid over a fixed term. This makes it a great option if you need a large amount of money upfront for one-time expenses, such as a home renovation or paying off debt. Since the loan is secured by your home, it typically comes with lower interest rates than unsecured loans.'
  }, {
    type: 'h2',
    text: 'Benefits of a Home Equity Loan'
  }, {
    type: 'ul',
    items: ["Access up to 75% of your home's appraised value", 'Lower interest rates compared to unsecured loans', 'Lump sum payment — ideal for large one-time expenses', 'No credit check required', 'Flexible repayment terms', 'Funds available in as little as 48 hours']
  }, {
    type: 'h2',
    text: 'Common Uses'
  }, {
    type: 'ul',
    items: ['Home renovations and improvements', 'Debt consolidation', 'Education expenses', 'Medical bills', 'Business investment', 'Emergency expenses']
  }, {
    type: 'steps',
    stepsTitle: 'The Home Equity Loan Process',
    steps: [{
      title: 'Consultation and Eligibility Check',
      description: 'The process begins with a consultation where we assess the equity in your home and discuss your financial goals. Our experts will help you understand how a home equity loan works and determine if this option is right for you. We focus on the value of your home, not your credit score or income, so approval is simpler and faster.'
    }, {
      title: 'Property Valuation',
      description: "To determine how much equity you can access, we'll arrange a professional appraisal of your property. This will give us an accurate estimate of your home's market value, which will help us calculate how much you can borrow through a home equity mortgage."
    }, {
      title: 'Loan Offer and Terms',
      description: "Once the appraisal is complete, we'll present you with a loan offer that includes the loan amount, interest rate, and repayment terms. This will ensure you understand the full details of the loan before moving forward."
    }, {
      title: 'Signing and Disbursement',
      description: 'After reviewing and signing the loan agreement, the funds will be disbursed according to your preferred method. Whether you need a lump sum for a renovation or debt consolidation, we make the process straightforward and efficient.'
    }, {
      title: 'Repayment',
      description: "With a home equity loan, you'll repay the loan over a fixed term with predictable monthly payments. The fixed interest rate ensures that your payments remain consistent throughout the life of the loan, making it easier to manage your finances."
    }]
  }, {
    type: 'h2',
    text: 'Why Choose EquityRich for Your Home Equity Loan?'
  }, {
    type: 'p',
    text: 'At EquityRich, we focus on equity-based lending, which means we offer home equity mortgages based on the value of your property, not your credit score or income. This allows us to offer competitive rates and flexible terms that are designed to meet your unique financial needs.'
  }, {
    type: 'p',
    text: 'Whether you need a second mortgage or a home equity loan, our team of experts is here to guide you through the process and help you access the funds you need.'
  }]
}, {
  slug: 'heloc',
  heroTitle: 'Access the Equity in Your Home with a HELOC',
  excerpt: "At EquityRich, we offer HELOCs (Home Equity Lines of Credit) that provide you with the flexibility to access funds whenever you need them. Whether you're planning home improvements, consolidating debt, or covering other expenses, a line of credit home equity can help you unlock the full potential of your property.",
  quickFacts: [{
    label: 'Credit Type',
    value: 'Revolving Line'
  }, {
    label: 'Max LTV',
    value: 'Up to 75%'
  }, {
    label: 'Interest',
    value: 'On Amount Used'
  }, {
    label: 'Credit Check',
    value: 'Not Required'
  }],
  content: [{
    type: 'h2',
    text: 'What Is a HELOC?'
  }, {
    type: 'p',
    text: 'A HELOC is a revolving line of credit that allows you to borrow against the equity in your home. Unlike a home equity loan, where you receive a lump sum, a HELOC gives you access to a revolving line of credit that you can draw from and repay again, much like a credit card.'
  }, {
    type: 'p',
    text: "A HELOC works by allowing you to borrow a certain percentage of your home's equity, up to a maximum limit. You can use the funds for a variety of purposes, and you only pay interest on the amount you borrow. Most equity lines of credit come with variable interest rates, which are typically lower than those of unsecured loans or credit cards."
  }, {
    type: 'h2',
    text: 'Benefits of a HELOC'
  }, {
    type: 'ul',
    items: ['Flexible revolving credit — borrow only what you need', 'Pay interest only on the amount you use', 'Reusable credit line as you repay', 'Lower rates than credit cards or personal loans', 'No credit check required', "Access up to 75% of your home's value"]
  }, {
    type: 'h2',
    text: 'Ideal Uses for a HELOC'
  }, {
    type: 'ul',
    items: ['Ongoing home renovation projects', 'Emergency fund access', 'Education costs', 'Debt management', 'Investment opportunities']
  }, {
    type: 'steps',
    stepsTitle: 'The HELOC Process',
    steps: [{
      title: 'Consultation and Eligibility Check',
      description: "The first step in securing a HELOC is a consultation with our team. We'll assess the equity in your property and help you understand how a HELOC can meet your financial needs. Our team will walk you through the application process and provide a clear picture of what to expect."
    }, {
      title: 'Property Valuation',
      description: "To determine how much you can borrow, we'll need to assess the current value of your property. This usually involves a professional appraisal. Based on the appraisal, we can determine the amount of equity available for your HELOC."
    }, {
      title: 'Loan Offer and Terms',
      description: "Once the property valuation is completed, we'll provide you with a loan offer. This will include the terms of the HELOC, such as the credit limit, interest rate, and repayment structure. We'll make sure you understand how the HELOC works, including how interest is charged and when repayments are due."
    }, {
      title: 'Signing and Disbursement',
      description: 'After reviewing and signing the loan agreement, the funds will be made available to you. You can access your HELOC through checks, debit cards, or online transfers, depending on how we set up the account. You can draw on the funds as needed, up to the agreed credit limit.'
    }, {
      title: 'Repayment',
      description: 'Repayment of a HELOC is based on the amount borrowed. You can make interest-only payments or choose to repay the principal as well. The flexible repayment terms make it easy to manage your finances while maintaining access to your credit line.'
    }]
  }, {
    type: 'h2',
    text: 'Why Choose EquityRich for Your HELOC?'
  }, {
    type: 'p',
    text: 'At EquityRich, we focus on equity-based lending, which means we lend based on the value of your property, not your income or credit score. We offer HELOCs with competitive HELOC rates in Canada and flexible repayment options that suit your unique financial situation.'
  }, {
    type: 'p',
    text: 'Whether you need a HELOC to fund home renovations, consolidate debt, or cover unexpected expenses, we provide a quick and easy process for accessing the equity in your home. Our team of experts will guide you through every step, ensuring you get the best possible solution for your needs.'
  }]
}, {
  slug: 'reverse-mortgages',
  heroTitle: 'Unlock the Value of Your Home With Reverse Mortgages',
  excerpt: "We are experts in equity-based lending, offering innovative reverse mortgage solutions that allow homeowners to tap into the value of their property without having to sell or move. Whether you're looking to supplement your retirement income, manage unexpected expenses, or simply enjoy your home without the stress of monthly payments, a reverse home mortgage from EquityRich could be the perfect solution.",
  quickFacts: [{
    label: 'Age Requirement',
    value: '55 and older'
  }, {
    label: 'Max LTV',
    value: 'Up to 70–75%'
  }, {
    label: 'Monthly Payments',
    value: 'None Required'
  }, {
    label: 'Tax Impact',
    value: 'Tax-Free Funds'
  }],
  content: [{
    type: 'h2',
    text: 'What Is a Reverse Mortgage?'
  }, {
    type: 'p',
    text: 'A reverse mortgage is a financial option that allows homeowners, typically aged 55 and older, to access the equity in their homes without selling. Unlike traditional mortgages, there are no monthly payments — this means you can continue living in your home while using the funds for whatever you need, such as home repairs, paying off debt, or supplementing retirement income.'
  }, {
    type: 'p',
    text: "With a reverse mortgage in Canada, you can access up to 70-75% of your home's value, which is significantly higher than many traditional lending options. Our reverse mortgage products are designed with flexibility in mind, allowing you to choose from a range of disbursement options, including lump sum payments or regular installments."
  }, {
    type: 'h2',
    text: 'Benefits of a Reverse Mortgage'
  }, {
    type: 'ul',
    items: ['No monthly mortgage payments required', 'Stay in your home while accessing equity', "Access up to 70-75% of your home's value", 'Tax-free funds — no impact on OAS or CPP', 'Available to homeowners aged 55 and older', 'Flexible disbursement: lump sum or installments']
  }, {
    type: 'h2',
    text: 'Is a Reverse Mortgage Right for You?'
  }, {
    type: 'p',
    text: 'A reverse mortgage is ideal for retirees who have significant equity in their home but limited monthly income. It allows you to maintain your lifestyle and cover expenses without selling your property or making monthly payments.'
  }, {
    type: 'steps',
    stepsTitle: 'The Reverse Mortgage Process',
    steps: [{
      title: 'Consultation and Eligibility Check',
      description: "The process begins with a consultation where we assess the equity in your property. We'll explain how a reverse mortgage works, outline your options, and help you determine if this solution is right for you. With no minimum credit score or income required, the approval process is streamlined and focused on the value of your home."
    }, {
      title: 'Property Valuation',
      description: "We work with professional appraisers to determine the current market value of your home. This is a key step in understanding how much equity you can access. Typically, reverse mortgages allow you to borrow up to 70-75% of your home's appraised value, but the exact amount will depend on your specific property."
    }, {
      title: 'Loan Offer and Terms',
      description: "Once we've determined your eligibility, we'll provide a loan offer with clear terms and conditions. You can choose how you want to receive the funds — whether as a lump sum, regular payments, or a line of credit."
    }, {
      title: 'Loan Agreement and Disbursement',
      description: "After reviewing and signing the loan agreement, the funds will be disbursed according to the agreed-upon method. From here, you can use the funds for your intended purpose, whether it's paying off debt, improving your home, or covering healthcare costs."
    }, {
      title: 'No Repayment Required Until You Sell or Move',
      description: 'Unlike traditional mortgages, reverse mortgages do not require monthly payments. The loan is repaid when the property is sold, or when you move out of the home for more than 12 months, or upon your passing. If you decide to sell the home, any remaining equity is returned to you or your heirs after the loan is paid off.'
    }]
  }, {
    type: 'h2',
    text: 'Why Choose EquityRich for Your Reverse Mortgage?'
  }, {
    type: 'p',
    text: "We pride ourselves on offering equity-based lending solutions that give you the freedom to access the funds you need without the stress of monthly payments. Whether you're interested in a reverse mortgage or home equity loan, we offer competitive loan-to-value ratios of up to 70-75%, and we can provide financing in first or second mortgage positions."
  }, {
    type: 'p',
    text: "Our flexible approach to private lending means that we can tailor solutions to meet your needs, and we can offer you a home equity mortgage with minimal hassle and with no age limitation. Whether you're interested in a second mortgage or a reverse mortgage, we're here to help you unlock the potential of your property."
  }]
}, {
  slug: 'second-mortgages',
  heroTitle: 'Unlock More Value with a Second Mortgage',
  excerpt: "At EquityRich, we specialize in providing flexible second mortgages that allow homeowners to access additional funds by leveraging the equity in their property. Whether you're looking to consolidate debt, finance a major renovation, or cover an unexpected expense, a second mortgage can help you unlock the financial flexibility you need, without the stress of selling your home.",
  quickFacts: [{
    label: 'Position',
    value: 'Subordinate Lien'
  }, {
    label: 'Credit Check',
    value: 'Not Required'
  }, {
    label: 'Income Verification',
    value: 'Not Required'
  }, {
    label: 'Funding Speed',
    value: 'As fast as 48 hrs'
  }],
  content: [{
    type: 'h2',
    text: 'How Does a Second Mortgage Work?'
  }, {
    type: 'p',
    text: "A second mortgage is a loan secured against your home's equity, in addition to your existing first mortgage. Essentially, it's a way to borrow additional funds using your home as collateral. With a second mortgage, you continue making payments on your primary mortgage, but the second loan is in a subordinate position. This means that if your home is sold, the first mortgage is paid off first, followed by the second."
  }, {
    type: 'h2',
    text: 'Benefits of a Second Mortgage'
  }, {
    type: 'ul',
    items: ['Access additional funds without refinancing your first mortgage', 'No credit check — approval based on home equity', 'Consolidate high-interest debt at lower rates', 'Finance renovations, investments, or major expenses', 'Keep your existing mortgage terms intact', 'Funds available in as little as 48 hours']
  }, {
    type: 'h2',
    text: 'Who Is a Second Mortgage For?'
  }, {
    type: 'p',
    text: "A second mortgage is ideal for homeowners who need a lump sum of money but don't want to break their existing mortgage. It's a popular choice for debt consolidation, home renovations, and accessing equity for investment purposes."
  }, {
    type: 'steps',
    stepsTitle: 'The Second Mortgage Process',
    steps: [{
      title: 'Consultation and Eligibility Check',
      description: 'The process starts with a consultation, where we assess your financial needs and the equity in your property. Our team will explain how a second mortgage works, discuss your options, and help you determine if this solution is right for you.'
    }, {
      title: 'Property Valuation',
      description: 'We work with professional appraisers to determine the current value of your home. This helps us calculate how much equity you can access through a second mortgage. The more equity you have, the more you can borrow.'
    }, {
      title: 'Loan Offer and Terms',
      description: "Once we have the property valuation, we'll provide you with a loan offer that outlines the terms and conditions. This includes the loan amount, interest rate, repayment schedule, and any applicable fees."
    }, {
      title: 'Signing and Disbursement',
      description: "After reviewing and signing the loan agreement, we'll disburse the funds according to your preferred method. Whether you need a lump sum for a one-time expense or a line of credit for ongoing needs, we offer flexible solutions."
    }, {
      title: 'Repayment',
      description: "With a second mortgage, you'll repay the loan over time. The repayment schedule will depend on the terms of the loan, but generally, you'll make regular monthly payments until the loan is paid off. If you're using the second mortgage for debt consolidation, your payments will be easier to manage, thanks to the competitive rates and structured terms."
    }]
  }, {
    type: 'h2',
    text: 'Why Choose EquityRich for Your Second Mortgage?'
  }, {
    type: 'p',
    text: 'At EquityRich, we focus on equity-based lending, which means we lend based on the value of your property, not your credit score or income. This makes us an ideal choice for those who may not qualify for traditional loans but still have significant equity in their homes. We offer first and second mortgages with competitive rates and flexible repayment options, ensuring that you get the funds you need on your terms.'
  }, {
    type: 'p',
    text: "Whether you're looking to consolidate debt, renovate your home, or pay for unexpected expenses, our second mortgage solutions are designed to help you achieve your financial goals with ease. We are your trusted partner in accessing your home's equity in the Greater Toronto Area (GTA)."
  }]
}, {
  slug: 'secondary-suite-mortgage',
  heroTitle: 'Unlock More Value with Secondary Suite Mortgages',
  excerpt: 'At Equity Rich, we assist homeowners in achieving maximum property value by providing financing for legal secondary suites and additional dwelling units (ADUs). Our flexible mortgage suite options help homeowners build additional living space and create rental income opportunities for their properties.',
  quickFacts: [{
    label: 'Property Type',
    value: 'Owner-Occupied'
  }, {
    label: 'Credit Check',
    value: 'Not Required'
  }, {
    label: 'Income Verification',
    value: 'Not Required'
  }, {
    label: 'LTV',
    value: 'High LTV Options'
  }],
  content: [{
    type: 'h2',
    text: 'What Is a Secondary Suite Mortgage?'
  }, {
    type: 'p',
    text: "A secondary suite mortgage serves homeowners who want to establish a legal secondary suite or additional dwelling unit (ADU) on their property. The financing supports both legal secondary suite conversions and new constructions. This type of mortgage is specifically designed to help homeowners in Ontario who want to add a rental unit to their property, whether it's a basement apartment, a garden suite, or a laneway house."
  }, {
    type: 'p',
    text: 'We differ from traditional lenders because we use your home equity instead of your income or credit score to make decisions. The approval process happens quickly, and we offer high loan-to-value (LTV) options while providing continuous support throughout the process.'
  }, {
    type: 'h2',
    text: 'Benefits of a Secondary Suite Mortgage'
  }, {
    type: 'ul',
    items: ['Finance the construction or conversion of a secondary suite', 'Approval based on home equity, not income or credit', 'High loan-to-value (LTV) options available', 'Generate rental income to offset mortgage costs', "Increase your property's overall value", 'Fast approval and funding process']
  }, {
    type: 'h2',
    text: 'Who Can Apply?'
  }, {
    type: 'p',
    text: "Ontario homeowners with sufficient equity in their property who want to add a legal secondary suite or ADU. Whether you're converting a basement or building a new unit, we can help finance the project."
  }, {
    type: 'steps',
    stepsTitle: 'How Does a Secondary Suite Mortgage Work?',
    steps: [{
      title: 'Property Review and Vision Planning',
      description: "Our team examines your property's equity and assesses its zoning and potential for both secondary suites and additional dwelling units. You qualify for this program if you possess untapped potential in your property, regardless of your credit score."
    }, {
      title: 'Simple Application with No Income Proof',
      description: "The equity-based lending model we operate with means we do not require minimum income or tax return verification. Your loan approval depends on both your home's value assessment and your detailed project plans."
    }, {
      title: 'Funding for Construction, Renovation, or Legalization',
      description: "We fund your project through all phases, including new construction and unit legalization, until you meet Ontario's building codes and secondary suite program standards."
    }, {
      title: 'Fast Disbursement, Flexible Terms',
      description: 'The approval process at our company occurs quickly, we offer financing up to 75% loan-to-value and can fund your project fast. Based on your available equity, you can choose the term and desired repayment structure.'
    }]
  }, {
    type: 'h2',
    text: 'Why Choose EquityRich for Your Secondary Suite Mortgage?'
  }, {
    type: 'p',
    text: 'Equity Rich operates as the main private lender across Ontario that provides financing solutions for secondary suite projects. We offer equity-based mortgages to all applicants with no age restrictions, no income requirements, no minimum credit score, and LTV up to 75%. The team at Equity Rich demonstrates expertise regarding financing of secondary suites.'
  }, {
    type: 'p',
    text: 'Our flexible mortgage solutions provide specific financial assistance to homeowners across the Greater Toronto Area who require non-traditional lending options. Our company has earned a reputation through years of experience in funding both secondary dwelling units and additional dwelling unit conversion projects by offering fast approval processes and transparent lending practices that produce tangible results.'
  }]
}, {
  slug: 'interest-only-mortgage',
  heroTitle: 'Unlock Financial Flexibility with Interest-Only Mortgages',
  excerpt: "At EquityRich, we specialize in unlocking home equity with creative, borrower-friendly solutions. Homeowners in the Greater Toronto Area gain total financial control through our interest-only mortgage program, especially when money flow adaptability is their most important concern. As a private and alternative mortgage lender, our approval process is based on the evaluation of your property's value and available equity rather than on your age, income, or credit score.",
  quickFacts: [{
    label: 'Payment Type',
    value: 'Interest Only'
  }, {
    label: 'Credit Check',
    value: 'Not Required'
  }, {
    label: 'Income Verification',
    value: 'Not Required'
  }, {
    label: 'Approval Basis',
    value: 'Property Equity'
  }],
  content: [{
    type: 'h2',
    text: 'What Is an Interest-Only Mortgage?'
  }, {
    type: 'p',
    text: 'An interest-only mortgage is a type of home loan where the borrower pays only the interest on the borrowed funds for a set period, without paying down any principal. This results in lower monthly payments during the interest-only period, giving you greater cash flow flexibility.'
  }, {
    type: 'p',
    text: 'In Canada, interest-only mortgage lenders offer borrowers different options for interest-only periods ranging from six months to several years. At EquityRich, our approval is based entirely on your property equity — not your age, income, or credit score — making this a powerful tool for homeowners who need to refinance, purchase homes, or consolidate debts.'
  }, {
    type: 'h2',
    text: 'Benefits of an Interest-Only Mortgage'
  }, {
    type: 'ul',
    items: ['Lower monthly payments during the interest-only period', 'Greater cash flow flexibility for homeowners', 'Approval based on property equity, not income or credit', 'Ideal for refinancing, purchasing, or debt consolidation', 'No age restrictions', 'Fast approval process']
  }, {
    type: 'h2',
    text: 'Who Is an Interest-Only Mortgage For?'
  }, {
    type: 'p',
    text: "An interest-only mortgage is ideal for homeowners who need to maximize short-term cash flow while leveraging their property's equity. It's a smart strategic solution for those who expect their financial situation to improve over time or who need flexibility during a transitional period."
  }, {
    type: 'steps',
    stepsTitle: 'How Does an Interest-Only Mortgage Work?',
    steps: [{
      title: 'Initial Interest-Only Term',
      description: 'During the first 6 months to several years, your monthly payments only cover the interest, calculated using competitive interest-only mortgage rates in Canada. Use our interest-only payment calculator to see how much you can save during this term.'
    }, {
      title: 'Equity-Based Approval Process',
      description: 'We lend based on the equity in your home, not your financial history. No income-based approvals. No credit requirements. Just a professional appraisal of your property value to determine eligibility.'
    }, {
      title: 'Flexible Loan Structuring',
      description: 'We offer interest-only mortgage options in both first and second positions, with loan-to-value ratios of up to 75%. Our flexibility makes refinancing or tapping into equity simpler and faster than traditional lenders.'
    }, {
      title: 'Refinancing Options',
      description: 'Can you refinance an interest-only mortgage? Absolutely. Once your interest-only term ends, you can convert it into a standard amortized loan or refinance into another interest-only or private lending solution with us.'
    }, {
      title: 'End of Term: Payoff or Extension',
      description: "When the interest-only term ends, you can choose to renew (if renewal is offered), refinance into principal plus interest payments, or pay off the loan in full. We'll work with you to ensure a seamless transition, always based on your evolving goals."
    }]
  }, {
    type: 'h2',
    text: 'Why Choose EquityRich for Your Interest-Only Mortgage?'
  }, {
    type: 'p',
    text: "Choosing Equity Rich means partnering with a lender that puts the focus back on you, and more specifically, your home's potential. We specialize in interest-only mortgages in Canada that are tailored to property owners in the Greater Toronto Area. Unlike banks and rigid lending institutions, our model is streamlined, fast, and flexible, offering private lending options based purely on home equity."
  }, {
    type: 'p',
    text: 'Our standout advantages are up to 70-75% LTV in both first and second positions, no income verification, no credit score requirements, no minimum age restrictions, competitive interest-only mortgage rates in Canada, and fast approvals and direct, personalized support. If your goal is short-term savings, long-term growth, or financial flexibility without jumping through hoops, Equity Rich makes it a great idea.'
  }]
}, {
  slug: 'alternative-mortgage-lenders',
  heroTitle: "Unlock Your Property's Potential with Leading Alternative Mortgage Lenders",
  excerpt: "At Equity Rich, we believe there's a smarter way to get approved for a mortgage — one that doesn't revolve around rigid rules or traditional banking hurdles. As one of the leading alternative mortgage lenders in the Greater Toronto Area, we offer flexible, equity-based financing that puts the focus on your property's value, not your income or credit score.",
  quickFacts: [{
    label: 'Approval Basis',
    value: 'Property Equity'
  }, {
    label: 'Credit Check',
    value: 'Not Required'
  }, {
    label: 'Income Verification',
    value: 'Not Required'
  }, {
    label: 'Funding Speed',
    value: 'As fast as 48 hrs'
  }],
  content: [{
    type: 'h2',
    text: 'What Is an Alternative Mortgage Lender?'
  }, {
    type: 'p',
    text: 'An alternative mortgage lender is a non-bank financial institution that provides mortgage financing outside of the traditional banking system. These lenders offer more flexible qualification criteria, making mortgages accessible to borrowers who may not meet the strict requirements of major banks.'
  }, {
    type: 'p',
    text: 'The process eliminates both paperwork requirements and extended waiting periods. Our alternative mortgage solutions help self-employed individuals, people who have been rejected by traditional banks, and anyone who needs a financing solution tailored to their actual needs rather than rigid bank criteria.'
  }, {
    type: 'h2',
    text: 'Benefits of Working with an Alternative Mortgage Lender'
  }, {
    type: 'ul',
    items: ['No rigid income or credit requirements', 'Approval based on property equity and value', 'Faster approval and funding process', 'Flexible terms tailored to your situation', 'Solutions for self-employed and non-traditional borrowers', 'Access to first and second mortgage products']
  }, {
    type: 'h2',
    text: 'Who Should Consider an Alternative Lender?'
  }, {
    type: 'p',
    text: "If you've been turned down by a bank, are self-employed, have irregular income, or simply need a faster and more flexible mortgage solution, EquityRich is your ideal alternative mortgage lender in the Greater Toronto Area."
  }, {
    type: 'steps',
    stepsTitle: 'How Do Alternative Mortgage Lenders Work?',
    steps: [{
      title: 'Property Evaluation, Not Credit Check',
      description: "We start by assessing your property's current market value. This becomes the basis of your loan, not your credit score or tax returns."
    }, {
      title: 'Application & Approval',
      description: "We'll guide you through a simple, transparent application process. No complicated forms or hoops to jump through, just clarity."
    }, {
      title: 'Flexible Loan Options',
      description: 'Choose between first or second mortgages, refinance options, or mortgage alternatives in Canada that allow interest-only payments or flexible terms.'
    }, {
      title: 'Fund Disbursement',
      description: 'Once approved, your funds are released promptly. This is ideal for time-sensitive purchases, renovations, or consolidating debt.'
    }, {
      title: 'Custom Repayment Terms',
      description: 'Our flexible terms mean you can tailor repayment to your financial comfort level while preserving your home equity.'
    }]
  }, {
    type: 'h2',
    text: 'Why Choose Equity Rich as Your Alternative Lender?'
  }, {
    type: 'p',
    text: "At Equity Rich, we're not just another alternative mortgage lender; we're your partner in unlocking the financial potential of your home. Unlike larger B lenders that still rely on old-school rules, we focus entirely on your property's value, offering more flexibility and fewer restrictions."
  }, {
    type: 'p',
    text: "We understand that life doesn't always fit into a box. That's why we've designed our lending model to be just as unique as you are, with a strong focus on up to 75% loan-to-value on first and second mortgages, property-based approvals across the GTA and Ontario, and private, judgment-free support from start to finish."
  }]
}, {
  slug: 'residential-mortgage',
  heroTitle: 'Unlock Homeownership with Equity-Based Residential Mortgages',
  excerpt: "At Equity Rich, we believe that owning a home or investing in property shouldn't be out of reach because of income statements or credit scores. That's why we offer equity-driven residential mortgage solutions across the Greater Toronto Area. Whether you're buying your first home, moving up, or refinancing, we focus on what matters most: your property's value.",
  quickFacts: [{
    label: 'Max LTV',
    value: 'Up to 75%'
  }, {
    label: 'Credit Check',
    value: 'Not Required'
  }, {
    label: 'Income Verification',
    value: 'Not Required'
  }, {
    label: 'Property Types',
    value: 'All Residential'
  }],
  content: [{
    type: 'h2',
    text: 'What Is a Residential Mortgage?'
  }, {
    type: 'p',
    text: "A residential mortgage is a loan you take out to buy or refinance a home, using the property itself as collateral. It could be a condo, a single-family home, or a multi-unit building — if it's residential, it qualifies. At EquityRich, we base our decisions on the value of your home, not your financial history."
  }, {
    type: 'p',
    text: 'That means no income requirements, no age limitations, and no need for a perfect credit score. With loan-to-value (LTV) ratios of up to 75% and a lending process built around your equity, we help open the door to real estate ownership on your terms. Our lending is flexible, fast, and built around your equity, not your age, job, or financial history.'
  }, {
    type: 'h2',
    text: 'Benefits of Our Residential Mortgage'
  }, {
    type: 'ul',
    items: ["Access up to 75% of your home's appraised value", 'No income requirements or age limitations', 'No credit score requirements', 'Available for purchases, refinancing, and renewals', 'Fast approval and funding', 'Flexible terms for all residential property types']
  }, {
    type: 'h2',
    text: 'Who Can Apply?'
  }, {
    type: 'p',
    text: "Any homeowner or home buyer in Ontario with sufficient property equity can apply. Whether you're purchasing your first home, refinancing an existing property, or investing in real estate, our equity-based residential mortgage is designed to work for you."
  }, {
    type: 'steps',
    stepsTitle: 'How Does a Residential Mortgage Work?',
    steps: [{
      title: 'Property Assessment',
      description: "The first step is assessing your home's current market value. Since our loans are equity-based, this is what determines how much you can borrow."
    }, {
      title: 'Application & Offer',
      description: "No complicated forms. Just tell us about your property; no credit checks or income statements are required. If you qualify, we'll send over a personalized offer that's clear and competitive."
    }, {
      title: 'Funding in First or Second Position',
      description: "Depending on your needs, you'll receive funds in either a first or second mortgage position. This flexibility gives you room to structure your finances smartly."
    }, {
      title: 'Use Funds How You Need',
      description: "Once approved, it's your equity; use it your way. Buy a home, pay off debts, renovate, or invest. We don't restrict how you put your funds to work."
    }, {
      title: 'Repayment on Your Schedule',
      description: "We'll tailor your repayment plan to suit your cash flow and timeline. No rigid rules. Just real solutions designed around you."
    }]
  }, {
    type: 'h2',
    text: 'Why Choose Equity Rich for Your Residential Mortgage?'
  }, {
    type: 'p',
    text: "Choosing Equity Rich means working with a lender who sees the value in your property, not just your financial history. We specialize in private mortgage lending across Toronto and the GTA, helping people who are often overlooked by the big banks. Whether you're self-employed, new to the country, in retirement, or dealing with a unique financial situation, we're here to help."
  }, {
    type: 'p',
    text: 'Our loans go up to 75% LTV, and we offer both first and second mortgage options with no minimum credit score. We also know the GTA market like the back of our hand. From Toronto and Mississauga to Brampton, Vaughan, and Scarborough, we build our solutions to fit your property, your city, and your goals.'
  }]
}, {
  slug: 'bridge-mortgage',
  heroTitle: 'Unlock the Equity in Your Home With Bridge Mortgages',
  excerpt: "Timing is everything in real estate, and when you're buying a new home before your current one sells, it can feel like walking a financial tightrope. At Equity Rich, we offer flexible bridge mortgage options that help customers switch between properties while maintaining financial stability. As a private lender specializing in equity-based lending, we provide custom-built bridge loan options to GTA homeowners who possess home equity yet need quick, short-term funding.",
  quickFacts: [{
    label: 'Loan Type',
    value: 'Short-Term'
  }, {
    label: 'Credit Check',
    value: 'Not Required'
  }, {
    label: 'Income Verification',
    value: 'Not Required'
  }, {
    label: 'Repayment',
    value: 'On Home Sale'
  }],
  content: [{
    type: 'h2',
    text: 'What Is a Bridge Mortgage?'
  }, {
    type: 'p',
    text: 'A bridge mortgage is a short-term financing solution that covers the gap between the purchase of a new home and the sale of your existing one. It allows you to access the equity in your current property to fund the down payment or purchase of your new home, without waiting for your existing home to sell.'
  }, {
    type: 'p',
    text: "The repayment of bridge loans in Canada occurs right after your current home sale transaction completes. Equity Rich provides bridge financing in Canada with equity-based approval — whether you're upgrading, downsizing, or relocating, our bridging mortgage service enables you to access your property value while skipping traditional income and credit requirements."
  }, {
    type: 'h2',
    text: 'Benefits of a Bridge Mortgage'
  }, {
    type: 'ul',
    items: ['Buy your new home before your current one sells', 'Access equity in your existing property immediately', 'No income or credit check required', 'Short-term solution with repayment on home sale', 'Avoid the stress of timing two transactions perfectly', 'Fast approval for GTA homeowners']
  }, {
    type: 'h2',
    text: 'Who Is a Bridge Mortgage For?'
  }, {
    type: 'p',
    text: 'A bridge mortgage is ideal for GTA homeowners who are in the process of buying a new property while their existing home is still on the market. If you have equity in your current home and need short-term financing to bridge the gap, we can help.'
  }, {
    type: 'steps',
    stepsTitle: 'How Does a Bridge Mortgage Work?',
    steps: [{
      title: 'Equity Review',
      description: 'We start by evaluating the current market value of your existing property to calculate the available equity amount. Our bridge loan lending model is strictly equity-based.'
    }, {
      title: 'Quick Application Process',
      description: 'Say goodbye to red tape. The application process requires no verification of income, credit score assessment, or age verification. Our company determines approval for bridge financing through home property valuation.'
    }, {
      title: 'Funding Released for New Home Purchase',
      description: 'The approval process leads to the distribution of a bridging mortgage that enables you to fund your new home purchase either partially or entirely.'
    }, {
      title: 'Repayment Upon Sale',
      description: 'Your bridge mortgage becomes fully payable when your current property sells without any penalty fees or extra charges.'
    }, {
      title: 'Support Throughout the Transition',
      description: 'We work closely with you and your real estate agent to coordinate everything perfectly so your relocation becomes peaceful and trouble-free.'
    }]
  }, {
    type: 'h2',
    text: 'Why Choose Equity Rich for Your Bridge Mortgage?'
  }, {
    type: 'p',
    text: 'EquityRich stands apart from other lenders because we base our bridge financing decisions on property value instead of financial history. Our lending approach differs from traditional banks, as we do not require income verification, credit score checks, or age restrictions. We provide financial assistance to self-employed individuals, retirees, and people with special financial situations through home equity-based loans.'
  }, {
    type: 'p',
    text: 'We provide maximum property borrowing potential through our 75% loan-to-value ratio. Your financing strategy becomes more flexible as our team specializes in both first and second mortgage positions. Equity Rich provides fast approval procedures and quick processing times that enable you to make swift decisions about your next home purchase.'
  }]
}, {
  slug: 'debt-consolidation-mortgage',
  heroTitle: 'Unlock Financial Freedom with a Debt Consolidation Mortgage',
  excerpt: 'At Equity Rich, we know the challenges of handling numerous high-interest debts, as they cause financial exhaustion. Our debt consolidation mortgage solutions are designed to assist Greater Toronto Area homeowners by utilizing property equity for better financial control. Through our equity-based lending model, you can merge various debts into one manageable mortgage without needing to show income, pass credit checks, or meet age restrictions that traditional lenders enforce.',
  quickFacts: [{
    label: 'Approval Basis',
    value: 'Property Equity'
  }, {
    label: 'Credit Check',
    value: 'Not Required'
  }, {
    label: 'Income Verification',
    value: 'Not Required'
  }, {
    label: 'Result',
    value: 'Single Payment'
  }],
  content: [{
    type: 'h2',
    text: 'What Is a Debt Consolidation Mortgage?'
  }, {
    type: 'p',
    text: 'A debt consolidation mortgage enables homeowners to combine multiple high-interest debts — such as credit cards and personal loans — into a single, lower-interest mortgage secured by their home. This creates a single payment plan with more beneficial conditions, reducing your overall interest costs and simplifying your finances.'
  }, {
    type: 'p',
    text: 'The process of obtaining a mortgage for debt consolidation provides homeowners with better interest rates and financial stability through refinancing either their first mortgage or securing a second mortgage. At Equity Rich, property equity is our sole basis for approving debt consolidation mortgages, which makes us an option for borrowers whom banks refuse to serve.'
  }, {
    type: 'h2',
    text: 'Benefits of a Debt Consolidation Mortgage'
  }, {
    type: 'ul',
    items: ['Combine multiple debts into one manageable payment', 'Lower interest rates compared to credit cards and personal loans', 'No income verification or credit check required', 'Approval based entirely on home equity', 'Reduce monthly payment obligations', 'Available as first or second mortgage']
  }, {
    type: 'h2',
    text: 'Who Can Benefit?'
  }, {
    type: 'p',
    text: "Homeowners in the Greater Toronto Area who are struggling with multiple high-interest debts and have sufficient equity in their property. Whether you've been turned down by a bank or simply want a better rate, our equity-based debt consolidation mortgage can help you regain financial control."
  }, {
    type: 'steps',
    stepsTitle: 'How Does a Debt Consolidation Mortgage Work?',
    steps: [{
      title: "Assess Your Property's Equity",
      description: 'Our first step involves determining the available equity amount in your home. We provide loans that reach between 70-75% of your property value in both primary and secondary positions.'
    }, {
      title: 'Combine All Eligible Debts',
      description: 'The process begins with debt documentation that includes full debt details, along with amounts and interest rates. Our team develops personalized debt consolidation with mortgage plans that use your available home equity to cover the maximum amount of your debt.'
    }, {
      title: 'Secure Your Equity-Based Loan',
      description: 'The loan approval process grants you access to a single mortgage sum that is calculated from your property value. Your high-interest debt obligations will receive complete payment through the loan proceeds.'
    }, {
      title: 'Make One Easy Payment',
      description: 'After approval, you will only need to pay a single mortgage payment at reduced rates instead of handling multiple accounts.'
    }, {
      title: 'Enjoy Peace of Mind',
      description: 'Our flexible debt consolidation mortgage solutions help you gain financial flexibility while allowing you to preserve your homeownership opportunities.'
    }]
  }, {
    type: 'h2',
    text: 'Why Choose Equity Rich as Your Debt Consolidation Mortgage Lender?'
  }, {
    type: 'p',
    text: 'Our non-traditional lending approach differentiates us from standard lenders. The equity-based lending approach of our company approves applications based on property values rather than checking personal income, age, or credit scores. Our team at Equity Rich specializes in providing first and second mortgage solutions to clients who failed to obtain loans from traditional lenders through our private and alternative lending services.'
  }, {
    type: 'p',
    text: 'We provide loan-to-value (LTV) options at 75%, which surpasses what many banks and private lenders offer in Ontario. We serve the Greater Toronto Area specifically because our local position allows us to deliver personalized service that larger financial institutions cannot duplicate. The expert team at Equity Rich is ready to assist you through debt consolidation to mortgage programs, as well as home loan refinancing and financial option guidance.'
  }]
}, {
  slug: 'commercial-mortgage',
  heroTitle: 'Fuel Your Business Growth with Commercial Mortgages',
  excerpt: "Getting the right financing is essential for business success in the Greater Toronto Area's (GTA) competitive commercial real estate market. With flexible, equity-based financing options catered to the potential of your property, EquityRich provides specialized commercial mortgage solutions. With a strong emphasis on the GTA, we are your committed private and alternative mortgage lender, working to unlock the funds you require to grow, invest, or stabilize your business endeavours across Canada.",
  quickFacts: [{
    label: 'Property Type',
    value: 'Commercial'
  }, {
    label: 'Approval Basis',
    value: 'Property Equity'
  }, {
    label: 'Credit Check',
    value: 'Not Required'
  }, {
    label: 'Coverage',
    value: 'GTA & Canada'
  }],
  content: [{
    type: 'h2',
    text: 'What Is a Commercial Mortgage?'
  }, {
    type: 'p',
    text: 'A commercial mortgage is a loan secured against non-residential properties — such as office buildings, retail spaces, industrial warehouses, and apartment buildings with 5 or more units. Businesses use commercial mortgages to refinance current debt, buy new real estate, or obtain funds for operations and strategic investments.'
  }, {
    type: 'p',
    text: 'In contrast to conventional bank loans, our commercial mortgage procedure prioritizes the equity of your property over individual financial indicators like income or credit score. This strategy increases the accessibility and adaptability of our solutions for a larger group of Canadian corporate investors and business owners.'
  }, {
    type: 'h2',
    text: 'Benefits of a Commercial Mortgage with EquityRich'
  }, {
    type: 'ul',
    items: ['Equity-based approval — no income or credit requirements', 'Financing for a wide range of commercial property types', 'Flexible terms tailored to your business needs', 'Fast approval and funding process', 'Available across the GTA and Canada', 'First and second mortgage positions available']
  }, {
    type: 'h2',
    text: 'Who Is This For?'
  }, {
    type: 'p',
    text: 'Business owners, investors, and entrepreneurs in the GTA who own or are purchasing commercial real estate. Whether you need to refinance, expand, or access working capital secured against your commercial property, EquityRich has a solution for you.'
  }, {
    type: 'steps',
    stepsTitle: 'How Our Commercial Mortgage Process Works',
    steps: [{
      title: 'Initial Consultation & Application',
      description: 'The process starts with a thorough consultation during which our knowledgeable commercial mortgage broker or agent learns about your unique financing requirements and property specifics. Your application will require little personal financial documentation and will primarily focus on the equity in your property.'
    }, {
      title: 'Property Assessment & Valuation',
      description: "Our staff performs a comprehensive appraisal after receiving your application to ascertain the available equity and current market value of your commercial property. Since our lending decisions are based on the property's intrinsic value within the GTA, this step is essential for determining the possible loan amount."
    }, {
      title: 'Tailored Loan Structuring',
      description: 'EquityRich will create a customized commercial mortgage offer based on the property assessment and your particular needs. In addition to options for first and second mortgage positions and competitive commercial mortgage rates in Canada, this offer will include information on the loan amount, interest rates, repayment terms, and any other pertinent conditions.'
    }, {
      title: 'Legal Review & Due Diligence',
      description: 'The legal and due diligence process starts as soon as you accept. To make sure the mortgage is correctly registered against the property, legal counsel examines all supporting documentation. Our team minimizes delays by working efficiently to finish this stage.'
    }, {
      title: 'Funding & Ongoing Support',
      description: 'The money is released after all requirements are satisfied and legal paperwork is completed, enabling you to move forward with your business plans. Throughout the duration of your commercial mortgage, EquityRich offers continuous support and direction, guaranteeing a solid and long-lasting collaboration.'
    }]
  }, {
    type: 'h2',
    text: 'Why Choose EquityRich for Your Commercial Mortgage?'
  }, {
    type: 'p',
    text: 'In the Greater Toronto Area, EquityRich is a prominent private and alternative commercial mortgage lender. Equity-based lending is one of our specialties. In Ontario, private lenders are becoming more and more important, and because we prioritize the value of your property, we can provide solutions that traditional banks might not be able to. We provide both first and second mortgage positions and go as high as 70-75% loan-to-value.'
  }, {
    type: 'p',
    text: "Most importantly, we remove typical obstacles: there is no minimum age requirement, no minimum credit score, and no income requirement. Regardless of your individual financial background, this inclusive approach guarantees that your business potential is acknowledged. EquityRich is your strategic partner for a commercial mortgage that is suited to the actual value of your assets, whether you're growing your retail presence or investing in Toronto's industrial sector."
  }]
}, {
  slug: 'property-investment-mortgage',
  heroTitle: 'Unlocking Your Investment Potential with a Property Investment Mortgage',
  excerpt: 'With a property investment mortgage, start your journey toward accumulating significant wealth through wise real estate purchases. At EquityRich, we are aware that a customized financing solution is necessary for a successful real estate investment in the vibrant Greater Toronto Area (GTA). Our specialty is offering strong, equity-based mortgage options that give you the confidence to take advantage of opportunities and grow your real estate holdings.',
  quickFacts: [{
    label: 'Property Type',
    value: 'Investment'
  }, {
    label: 'Approval Basis',
    value: 'Property Equity'
  }, {
    label: 'Credit Check',
    value: 'Not Required'
  }, {
    label: 'Income Verification',
    value: 'Not Required'
  }],
  content: [{
    type: 'h2',
    text: 'What Is a Property Investment Mortgage?'
  }, {
    type: 'p',
    text: 'A property investment mortgage is a type of financial instrument intended for the purpose of purchasing homes or businesses for capital gains or rental income. These loans, which differ from standard residential mortgages, are designed to take into account the particular financial characteristics of investment properties, including potential rental income and property value appreciation.'
  }, {
    type: 'p',
    text: 'Getting the right investment property can be crucial to reaching your financial goals in the quickly changing Canadian real estate market, especially in the Greater Toronto Area. With a thriving market with steady demand and rising values, you can increase your presence in this profitable industry by using your current equity or obtaining alternative financing through a well-structured mortgage for investment property.'
  }, {
    type: 'h2',
    text: 'Benefits of a Property Investment Mortgage'
  }, {
    type: 'ul',
    items: ['Equity-based approval — no income or credit checks', 'Finance residential or commercial investment properties', 'Leverage existing equity to grow your portfolio', 'Competitive loan-to-value ratios', 'Fast approval and funding', 'Available for first and second mortgage positions']
  }, {
    type: 'h2',
    text: 'Who Is This For?'
  }, {
    type: 'p',
    text: "Real estate investors and homeowners in the GTA who want to expand their property portfolio. Whether you're purchasing your first investment property or adding to an existing portfolio, our equity-based approach makes it possible regardless of your income or credit history."
  }, {
    type: 'steps',
    stepsTitle: 'How Does Property Investment Work?',
    steps: [{
      title: 'Application and Property Evaluation',
      description: 'We evaluate the possible investment property and your overall financial situation when you submit your application, which starts the process. EquityRich prioritizes the equity in your property over strict income or credit score requirements, in contrast to traditional lenders. A greater variety of investors in the Greater Toronto Area can now access the market thanks to this innovative strategy.'
    }, {
      title: 'Equity-Based Assessment',
      description: 'Equity-based lending is our main area of interest. We assess the equity that currently exists in your property or the property you plan to purchase. This gives us a major edge in the private lending market by enabling us to provide competitive mortgage rates on investment properties and loan-to-value (LTV) ratios of up to 70-75%.'
    }, {
      title: 'Tailored Loan Structuring',
      description: 'We create a personalized investment property mortgage loan that fits your investment objectives based on our equity assessment. We provide first and second position mortgages, giving you the freedom to efficiently use the value of your property as leverage. This customized strategy sets us apart from traditional lenders.'
    }, {
      title: 'No Traditional Hurdles',
      description: "Our dedication to reducing conventional borrowing barriers is one of EquityRich's primary differentiators. Unlike reverse mortgages, we don't have minimum age restrictions, minimum income, or credit score requirements. Getting a property for an investment is easier and more effective with this simplified process."
    }, {
      title: 'Funding and Property Acquisition',
      description: 'The money is released after approval, allowing you to finish buying the property. In the competitive GTA market, our streamlined procedure guarantees that you can act swiftly on investment opportunities, enabling you to profit from timely real estate endeavours.'
    }]
  }, {
    type: 'h2',
    text: 'Why Choose EquityRich for Your Property Investment?'
  }, {
    type: 'p',
    text: "Selecting EquityRich for your Greater Toronto Area property investment entails working with an expert who is knowledgeable about the ins and outs of real estate investing and the equity value of your property. We are a committed private/alternative mortgage lender that is in a unique position to provide solutions where traditional institutions might not be able to. We are not a traditional bank. Our strength is our equity-based lending model, which enables us to approve financing solutions by considering your asset's actual value rather than just your financial history. In a dynamic market where quick access to capital is essential, this strategy is especially beneficial."
  }, {
    type: 'p',
    text: 'Unmatched flexibility in funding your real estate goals is offered by our dedication to high loan-to-value ratios and our capacity to obtain both first and second position mortgages. By removing typical obstacles like age limitations, minimum credit scores, and income verification, we enable a wider range of investors to invest in real estate. EquityRich offers the strategic financial leverage you require to grow your portfolio and optimize your returns, regardless of your level of experience as an investor or your desire to purchase your first investment property in Canada.'
  }]
}, {
  slug: 'retirement-mortgage',
  heroTitle: "Unlock Your Home's Potential: Retirement Mortgage",
  excerpt: 'At Equity Rich, we recognize that your house is a valuable asset with unrealized potential, not just a place to live. Our retirement mortgage service has been carefully crafted to assist people in leveraging the equity of their properties without the typical limitations that come with traditional lending or even reverse mortgages. By offering financial solutions that adjust to your particular situation, we enable you to access the wealth kept in your home.',
  quickFacts: [{
    label: 'Age Requirement',
    value: 'None'
  }, {
    label: 'Monthly Payments',
    value: 'Flexible'
  }, {
    label: 'Credit Check',
    value: 'Not Required'
  }, {
    label: 'Approval Basis',
    value: 'Property Equity'
  }],
  content: [{
    type: 'h2',
    text: 'What Is a Retirement Mortgage?'
  }, {
    type: 'p',
    text: 'A retirement mortgage is a financial tool that lets homeowners convert home equity into easily accessible funds. Unlike traditional equity-based lending, in which lenders mainly consider income and credit scores, our primary focus is on the equity and value of your home, making it a viable option for a wider range of retirees and near-retirees.'
  }, {
    type: 'p',
    text: 'Those who want to increase their financial liquidity without having to sell their home or take on additional monthly mortgage payments will find this service especially helpful. Equity Rich sets itself apart from reverse mortgages for the retirement market by providing unmatched flexibility, such as higher loan-to-value ratios and no minimum age requirements, which makes it a genuinely unique offering in the Canadian lending market.'
  }, {
    type: 'h2',
    text: 'Benefits of a Retirement Mortgage'
  }, {
    type: 'ul',
    items: ['No minimum age requirement — unlike reverse mortgages', 'Access equity without selling your home', 'Higher loan-to-value ratios than traditional options', 'No income or credit check required', 'Flexible repayment options', 'Tax-efficient way to access home equity']
  }, {
    type: 'h2',
    text: 'Who Is a Retirement Mortgage For?'
  }, {
    type: 'p',
    text: 'Homeowners approaching or in retirement who want to access the equity in their home without the restrictions of a reverse mortgage. Whether you need to supplement retirement income, cover healthcare costs, or fund a major expense, our retirement mortgage offers a flexible, equity-based solution.'
  }, {
    type: 'steps',
    stepsTitle: 'How Does Retirement Mortgage Work?',
    steps: [{
      title: 'Initial Consultation and Eligibility Assessment',
      description: 'Start with a one-on-one consultation during which we go over your financial objectives and determine the equity in your property. Since the value of your house is our main concern, the initial eligibility check is simple and less invasive than with traditional lending.'
    }, {
      title: 'Property Valuation',
      description: 'To ascertain the current market value of your Greater Toronto Area property, a professional appraisal is carried out. In order to determine the maximum loan amount based on the equity in your home, this valuation is an essential step.'
    }, {
      title: 'Loan Offer and Terms',
      description: 'Equity Rich will provide a comprehensive loan offer based on your unique needs and the property appraisal. To ensure total transparency, this will specify the loan amount, terms, interest rates, and how the money can be accessed.'
    }, {
      title: 'Independent Legal Advice',
      description: 'We need independent legal counsel as part of our dedication to your comprehension and safety. This guarantees that you understand every detail of the loan agreement before moving forward, which is an essential step for any retirement mortgage loan.'
    }, {
      title: 'Agreement and Funding Disbursement',
      description: 'The required legal documents are completed after the terms have been comprehended and accepted. After that, money is distributed, giving you the money you require for the things you want to do.'
    }, {
      title: 'Future Repayment',
      description: 'When the last homeowner sells the house, moves out permanently (for example, to a long-term care facility), or passes away, the loan becomes due. At that time, the loan is paid back from the estate or the sale of the house, including any interest that has accumulated.'
    }]
  }, {
    type: 'h2',
    text: 'Why Choose Equity Rich for Retirement Mortgage?'
  }, {
    type: 'p',
    text: 'Getting a mortgage entails working with a prominent private and alternative mortgage lender with a strong presence in the Greater Toronto Area real estate market. Our steadfast dedication to equity-based lending is what sets us apart. While many lenders focus on factors like age, income, and credit scores, we give more weight to the tangible asset you own: the equity in your home. Homeowners who might encounter difficulties with traditional lenders can now access home equity without the usual obstacles, thanks to our innovative financial solutions.'
  }, {
    type: 'p',
    text: 'Our strategic approach to empowering homeowners is demonstrated by our competitive loan-to-value ratios. We are aware that people\'s circumstances can change, and our "no minimum age" policy and emphasis on property value rather than personal characteristics make us the best option for flexible and affordable mortgages for retirement with low incomes and all homeowners looking to take advantage of the inherent value of their homes.'
  }]
}, {
  slug: 'spousal-buyout-mortgage',
  heroTitle: 'Unlock the Equity in Your Home With A Spousal Buyout Mortgage',
  excerpt: "At EquityRich, we help homeowners find clarity and control during these transitions. Our spousal buyout mortgage is specifically designed for those who want to stay in their home and buy out their ex-spouse's share without the usual roadblocks from traditional lenders. We focus entirely on equity-based lending, offering up to 70-75% loan-to-value (LTV) in both first and second mortgage positions.",
  quickFacts: [{
    label: 'Max LTV',
    value: 'Up to 70–75%'
  }, {
    label: 'Credit Check',
    value: 'Not Required'
  }, {
    label: 'Income Verification',
    value: 'Not Required'
  }, {
    label: 'Age Restriction',
    value: 'None'
  }],
  content: [{
    type: 'h2',
    text: 'What Is a Spousal Buyout Mortgage?'
  }, {
    type: 'p',
    text: "A spousal buyout mortgage allows one partner to refinance the existing mortgage and buy out the other's interest in the property after a separation or divorce. It's a solution for anyone who wants to keep the home, provide stability for children, or avoid selling a valuable property during an already difficult time."
  }, {
    type: 'p',
    text: "In Canada, spousal buyout mortgages are commonly used during divorce proceedings and often cover not only the balance of the existing mortgage but also the payout to the departing spouse, legal costs, and other related expenses. It's a comprehensive financial solution that helps you move forward with confidence."
  }, {
    type: 'h2',
    text: 'Benefits of a Spousal Buyout Mortgage'
  }, {
    type: 'ul',
    items: ['Keep your home during separation or divorce', 'Buy out your ex-spouse without selling the property', 'No income checks or credit score minimums', 'No age restrictions', 'Up to 70-75% LTV in first and second mortgage positions', 'Fast and straightforward approval process']
  }, {
    type: 'h2',
    text: 'Who Is This For?'
  }, {
    type: 'p',
    text: "Homeowners in Ontario going through a separation or divorce who want to retain ownership of the family home. If you have sufficient equity in your property, EquityRich can help you buy out your ex-spouse's share without the typical barriers imposed by traditional lenders."
  }, {
    type: 'steps',
    stepsTitle: 'How Does a Spousal Buyout Mortgage Work?',
    steps: [{
      title: "We Start by Assessing Your Home's Equity",
      description: "First, we take a close look at your home's current market value to see how much equity you've built up. From there, we determine your loan eligibility based entirely on that equity, not your income, credit score, or age."
    }, {
      title: "Borrow Up to 70-75% of Your Home's Value",
      description: "Our equity-based lending model lets us offer higher loan-to-value ratios than most traditional lenders, up to 75%. That means you can cover the remaining mortgage, your spouse's share, and even some legal costs all in one go."
    }, {
      title: 'No Credit, Income, or Age Checks',
      description: "Whether you're self-employed, retired, or just getting back on your feet, we won't ask for pay stubs, credit reports, or proof of income. We focus only on your property's value, making this option far more accessible than a standard bank mortgage."
    }, {
      title: 'Flexible Options: First or Second Mortgage',
      description: "Need to refinance completely or just add a second mortgage to access funds? No problem. We'll build a financing solution around your current mortgage setup so it fits your needs, not the other way around."
    }, {
      title: 'We Coordinate the Legal Side for You',
      description: 'Our team works directly with your lawyer to handle the title transfer and buyout paperwork, making sure everything is done properly and on time. We aim to make this part as stress-free and smooth as possible.'
    }, {
      title: 'Quick Turnaround When You Need It Most',
      description: "We understand how time-sensitive divorce settlements can be. That's why we move fast; many clients get approval and funding within just a few business days, so you're not left waiting when it matters most."
    }]
  }, {
    type: 'h2',
    text: 'How to Buy Out Your Spouse in a Divorce'
  }, {
    type: 'p',
    text: "If you're looking to keep your home after a separation, the first step is putting a formal separation agreement in place. This document outlines how your assets, like the family home, will be divided. Once that's done, you'll need to get a professional appraisal to find out your home's current market value. From there, you can figure out what your spouse's share of the equity is and how much you'll need to pay them out."
  }, {
    type: 'p',
    text: "That's where a spousal buyout mortgage comes in. It allows you to refinance your current mortgage and borrow the extra funds needed to buy out your ex-spouse's share, all without the rigid rules and delays that come with traditional banks. At EquityRich, we make the process as straightforward as possible. Our equity-based approach is fast, flexible, and built around your needs, so you can become the sole owner of your home and start this next chapter with confidence."
  }]
}, {
  slug: 'self-employed-mortgage',
  heroTitle: 'Access the Equity in Your Home with a Mortgage for Self-Employed',
  excerpt: 'Professionals who work for themselves encounter many obstacles when attempting to secure conventional mortgage financing. To make the mortgage process easier for our clients, EquityRich concentrates on home equity. Our equity-based mortgage solutions offer quick and easy, flexible home financing options to business owners, including independent contractors and small business owners.',
  quickFacts: [{
    label: 'Income Proof',
    value: 'Not Required'
  }, {
    label: 'Credit Check',
    value: 'Not Required'
  }, {
    label: 'Approval Basis',
    value: 'Property Equity'
  }, {
    label: 'Funding Speed',
    value: 'As fast as 48 hrs'
  }],
  content: [{
    type: 'h2',
    text: 'What Is a Self-Employed Mortgage?'
  }, {
    type: 'p',
    text: "Self-employed mortgages in Canada provide financing solutions to business owners who need to access funds, while banks ask for extensive income documentation that self-employed individuals often can't provide. At EquityRich, we provide self-employed mortgages through property equity instead of income or credit checks."
  }, {
    type: 'p',
    text: 'Your ability to qualify for a self-employed mortgage with us depends on the equity in your home, not your tax returns or business financials. This makes our solution ideal for independent contractors, freelancers, small business owners, and anyone with non-traditional income sources.'
  }, {
    type: 'h2',
    text: 'Benefits of a Self-Employed Mortgage'
  }, {
    type: 'ul',
    items: ['No income documentation or proof of employment required', 'No credit score requirements', 'Approval based entirely on home equity', 'Available for business owners, contractors, and freelancers', 'Fast approval and funding in as little as 48 hours', 'No age restrictions']
  }, {
    type: 'h2',
    text: 'Who Qualifies?'
  }, {
    type: 'p',
    text: "Any self-employed homeowner in Ontario with sufficient equity in their property. Whether you're a sole proprietor, independent contractor, or small business owner, our equity-based approach means your business structure or income variability won't prevent you from accessing the financing you need."
  }, {
    type: 'steps',
    stepsTitle: 'Process of Mortgage for Self-Employed',
    steps: [{
      title: 'Determine Borrowing Needs',
      description: 'The initial step involves determining the loan amount needed and the intended use of the funds, which can be home purchase, refinancing, business, or personal equity access.'
    }, {
      title: 'Gather Documentation',
      description: 'Self-employed applicants need to present financial documents to lenders for assessment. The documentation requirements for self-employed applicants differ between lenders but typically include tax returns, bank statements, proof of business ownership, and additional financial records.'
    }, {
      title: 'Property Appraisal',
      description: 'The lender needs a professional appraisal to establish the current market value of the property. The appraisal process determines property value, which helps calculate available equity and establishes the loan-to-value ratio.'
    }, {
      title: 'Loan Assessment and Terms',
      description: 'The lender examines the application documents and property appraisal to create mortgage terms for the applicant. The proposed mortgage terms include the loan amount and interest rate, together with repayment terms and all applicable fees.'
    }, {
      title: 'Legal Review and Funding',
      description: 'The preparation of legal documents follows the terms agreement before they undergo review. The mortgage becomes final after all legal requirements are satisfied and the funds become available for disbursement.'
    }]
  }, {
    type: 'h2',
    text: 'Why Choose EquityRich For Your Mortgage for Self-Employed?'
  }, {
    type: 'p',
    text: 'Our company provides services that move beyond what conventional lenders offer. Our equity-based lending model serves as a dependable financing solution for self-employed and mortgage applicants throughout the GTA. Our private mortgage lending service stands out from others because we verify no income and have no minimum age requirement, as well as we offer flexible 75% LTV lending that focuses on your property instead of paperwork.'
  }, {
    type: 'p',
    text: 'We provide self-employed mortgage solutions that address the needs of business owners and independent workers while bypassing traditional bank delays and complications. EquityRich serves as your trusted mortgage provider for all your needs, including reverse mortgages and private loans and second mortgages throughout Canada.'
  }]
}];
const fallbackService: ServiceData = {
  slug: '',
  heroTitle: 'Service Not Found',
  excerpt: 'This service page could not be found. Please return to our services page.',
  quickFacts: [],
  content: []
};
function renderContent(section: ContentSection, index: number) {
  if (section.type === 'h2') {
    return <h2 key={index} className="text-2xl md:text-3xl font-serif text-gray-900 font-[500] mt-10 mb-4" data-id="element-854">
        {section.text}
      </h2>;
  }
  if (section.type === 'p') {
    return <p key={index} className="text-gray-700 leading-relaxed mb-4" data-id="element-855">
        {section.text}
      </p>;
  }
  if (section.type === 'ul' && section.items) {
    return <ul key={index} className="space-y-3 mb-6" role="list" data-id="element-856">
        {section.items.map((item, i) => <li key={i} className="flex items-start" data-id="element-857">
            <CheckCircleIcon className="h-5 w-5 text-primary mr-3 mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-858" />
            <span className="text-gray-700" data-id="element-859">{item}</span>
          </li>)}
      </ul>;
  }
  if (section.type === 'steps' && section.steps) {
    return <div key={index} className="mt-12 mb-8" data-id="element-860">
        <h2 className="text-2xl md:text-3xl font-serif text-gray-900 font-[500] mb-8" data-id="element-861">
          {section.stepsTitle}
        </h2>
        <div className="relative" data-id="element-862">
          {/* Vertical timeline line */}
          <div className="absolute left-[19px] top-6 bottom-6 w-[2px] bg-gradient-to-b from-primary/30 via-accent/40 to-primary/10" aria-hidden="true" data-id="element-863" />
          <div className="space-y-0" data-id="element-864">
            {section.steps.map((step, i) => <motion.div key={i} className="relative flex gap-5 pb-8 last:pb-0" initial={{
            opacity: 0,
            x: -16
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-40px'
          }} transition={{
            duration: 0.4,
            delay: i * 0.1,
            ease: 'easeOut'
          }} data-id="element-865">
                {/* Step number circle */}
                <div className="relative z-10 flex-shrink-0 w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold shadow-md" data-id="element-866">
                  {i + 1}
                </div>
                {/* Step content */}
                <div className="pt-1.5 flex-1" data-id="element-867">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-868">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-[15px]" data-id="element-869">
                    {step.description}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </div>;
  }
  return null;
}
export function ServiceDetailPage() {
  const {
    serviceSlug
  } = useParams<{
    serviceSlug: string;
  }>();
  const service = services.find(s => s.slug === serviceSlug) || fallbackService;
  return <main className="w-full" data-id="element-870">
      {/* Hero Section */}
      <section className="w-full relative overflow-hidden bg-gradient-to-br from-[#07493a] to-[#1a6b55] py-20 px-4" data-id="element-871">
        {/* Radial gold glow on the left */}
        <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse 50% 70% at 10% 60%, rgba(201,169,97,0.15) 0%, rgba(201,169,97,0.06) 40%, transparent 70%)'
      }} aria-hidden="true" data-id="element-872" />

        <div className="max-w-7xl mx-auto relative z-10" data-id="element-873">
          <Link to="/services" className="inline-flex items-center text-sm text-white/70 hover:text-white transition-colors mb-8 font-medium" data-id="element-874">
            <ArrowLeftIcon className="h-4 w-4 mr-1.5" aria-hidden="true" data-id="element-875" />
            All Services
          </Link>

          <h1 className="text-3xl md:text-5xl lg:text-6xl font-serif text-white font-[500] leading-tight mb-6 max-w-4xl" data-id="element-876">
            {service.heroTitle}
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl leading-relaxed" data-id="element-877">
            {service.excerpt}
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="w-full bg-cream py-16 px-4" data-id="element-878">
        <div className="max-w-7xl mx-auto" data-id="element-879">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-880">
            {/* Main Content */}
            <div className="lg:col-span-2" data-id="element-881">
              <div className="bg-white rounded-2xl shadow-sm px-8 pb-8 pt-2 md:px-10 md:pb-10 md:pt-2 [&>h2:first-child]:mt-2" data-id="element-882">
                {service.content.map((section, index) => renderContent(section, index))}
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6" data-id="element-883">
              {/* Quick Facts */}
              {service.quickFacts.length > 0 && <div className="bg-white rounded-2xl shadow-sm p-6" data-id="element-884">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-3 border-b border-gray-100" data-id="element-885">
                    Quick Facts
                  </h3>
                  <ul className="space-y-4" role="list" data-id="element-886">
                    {service.quickFacts.map((fact, i) => <li key={i} className="flex justify-between items-center" data-id="element-887">
                        <span className="text-sm text-gray-500" data-id="element-888">
                          {fact.label}
                        </span>
                        <span className="text-sm font-semibold text-primary bg-primary/5 px-3 py-1 rounded-full" data-id="element-889">
                          {fact.value}
                        </span>
                      </li>)}
                  </ul>
                </div>}

              {/* CTA Card */}
              <div className="bg-gradient-to-br from-[#07493a] to-[#1a6b55] rounded-2xl p-6 text-center" data-id="element-890">
                <h3 className="text-xl font-serif text-white font-[500] mb-3" data-id="element-891">
                  Ready to Get Started?
                </h3>
                <p className="text-gray-300 text-sm mb-5 leading-relaxed" data-id="element-892">
                  No credit check. No income verification. Funds in as little as
                  48 hours.
                </p>
                <Button variant="primary" size="md" icon href="/#form" className="w-full justify-center" data-id="element-893">
                  Get Free Assessment
                </Button>
                <a href="tel:+16476679957" className="mt-3 flex items-center justify-center text-sm text-gray-300 hover:text-white transition-colors" data-id="element-894">
                  <PhoneIcon className="h-4 w-4 mr-1.5" aria-hidden="true" data-id="element-895" />
                  +1-647-667-9957
                </a>
              </div>

              {/* Other Services */}
              <div className="bg-white rounded-2xl shadow-sm p-6" data-id="element-896">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 pb-3 border-b border-gray-100" data-id="element-897">
                  Other Services
                </h3>
                <ul className="space-y-1" role="list" data-id="element-898">
                  {services.filter(s => s.slug !== serviceSlug).map(s => <li key={s.slug} data-id="element-899">
                        <Link to={`/services/${s.slug}`} className="flex items-center text-sm text-gray-600 hover:text-primary transition-colors py-1.5 group" data-id="element-900">
                          <ArrowLeftIcon className="h-3.5 w-3.5 mr-2 rotate-180 text-primary opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" aria-hidden="true" data-id="element-901" />
                          <span className="group-hover:translate-x-0.5 transition-transform" data-id="element-902">
                            {s.heroTitle.replace(/^(Unlock(ing)?|Access|Fuel|Get)\s+/i, '').replace(/^(the\s+|Your\s+|Financial\s+|Homeownership\s+with\s+Equity-Based\s+)/i, '').replace(/^.*?with\s+(a\s+|an\s+)?/i, '').replace(/^.*?:\s+/i, '').trim()}
                          </span>
                        </Link>
                      </li>)}
                </ul>
                <Link to="/services" className="mt-4 inline-flex items-center text-sm font-medium text-primary hover:text-accent transition-colors" data-id="element-903">
                  View All Services
                  <ArrowLeftIcon className="h-4 w-4 ml-1.5 rotate-180" aria-hidden="true" data-id="element-904" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="w-full relative overflow-hidden bg-gradient-to-br from-[#07493a] to-[#1a6b55] py-20 px-4" data-id="element-905">
        <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse 60% 80% at 80% 20%, rgba(201,169,97,0.15) 0%, rgba(201,169,97,0.06) 40%, transparent 70%)'
      }} aria-hidden="true" data-id="element-906" />
        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-907">
          <h2 className="text-3xl md:text-4xl font-serif text-white font-[500] mb-4" data-id="element-908">
            Ready to Access Your Home Equity?
          </h2>
          <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto" data-id="element-909">
            No credit check. No income verification. Our team is ready to help
            you unlock the value of your home.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-910">
            <Button variant="primary" size="lg" icon href="/#form" data-id="element-911">
              Get My Free Assessment
            </Button>
            <a href="/contact" className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium border-2 border-white text-white hover:bg-white hover:text-primary rounded-xl transition-all" data-id="element-912">
              Contact Us
            </a>
          </div>
        </div>
      </section>
    </main>;
}